// frontend/src/app/services/plan-feature.service.ts
import { Injectable, computed, inject } from '@angular/core';
import { AuthService } from './auth.service';
import {
  FEATURES,
  PLAN_FEATURES,
  PLAN_LABELS,
  PLAN_LIMITS,
  type FeatureKey,
  type PlanId,
  getMinPlanForFeature,
} from '../shared/config/plan-features';

/**
 * Service centralisé pour le feature gating.
 * Détermine le plan effectif et vérifie l'accès aux fonctionnalités.
 *
 * Logique:
 * - Plan payant → features du plan
 * - Plan free + trial actif → features Pro (effectivePlan='pro' from backend)
 * - Plan free + trial expiré → features free
 */
@Injectable({
  providedIn: 'root',
})
export class PlanFeatureService {
  private authService = inject(AuthService);

  /** Plan effectif (depuis le backend, inclut la logique trial→pro) */
  readonly effectivePlan = computed<PlanId>(() => {
    const user = this.authService.currentUser();
    // effectivePlan est calculé par le backend (getEffectivePlan())
    const effective = (user as any)?.effectivePlan;
    if (effective) return effective as PlanId;
    // Fallback: plan brut
    const plan = (user as any)?.plan;
    return (plan || 'free') as PlanId;
  });

  /** Plan sélectionné lors de l'inscription (pour la conversion post-trial) */
  readonly selectedPlan = computed<PlanId | null>(() => {
    const user = this.authService.currentUser();
    return ((user as any)?.selectedPlan as PlanId) || null;
  });

  /** Plan de facturation réel (pas l'effectif) */
  readonly billingPlan = computed<PlanId>(() => {
    const user = this.authService.currentUser();
    return ((user as any)?.plan || 'free') as PlanId;
  });

  /** L'utilisateur est en période d'essai */
  readonly isTrial = computed<boolean>(() => {
    return this.billingPlan() === 'free' && this.effectivePlan() !== 'free';
  });

  /**
   * Vérifie si l'utilisateur a accès à une fonctionnalité.
   */
  canAccess(feature: FeatureKey): boolean {
    const plan = this.effectivePlan();
    return PLAN_FEATURES[plan]?.[feature] ?? false;
  }

  /**
   * Signal computed pour usage dans les templates.
   * Usage: planFeatureService.hasAccess(FEATURES.EXPORT_PDF)()
   */
  hasAccess(feature: FeatureKey) {
    return computed(() => {
      const plan = this.effectivePlan();
      return PLAN_FEATURES[plan]?.[feature] ?? false;
    });
  }

  /**
   * Retourne le plan minimum pour accéder à une fonctionnalité.
   */
  getRequiredPlan(feature: FeatureKey): PlanId {
    return getMinPlanForFeature(feature);
  }

  /**
   * Retourne le label du plan minimum requis.
   */
  getRequiredPlanLabel(feature: FeatureKey): string {
    const plan = getMinPlanForFeature(feature);
    return PLAN_LABELS[plan];
  }

  /**
   * Limite d'événements par mois pour le plan effectif.
   * null = illimité
   */
  readonly maxEventsPerMonth = computed<number | null>(() => {
    const plan = this.effectivePlan();
    return PLAN_LIMITS[plan]?.maxEventsPerMonth ?? null;
  });
}
